<script lang="ts">
  import { t } from '$core/i18n';
  import { STANDARD_TAXONOMY } from '$domain/taxonomy';
  import type { AttemptResult } from '$domain/types';
  import type { SessionStore } from '$features/session';
  import { Button } from '$ui/primitives/Button';
  import { Select } from '$ui/primitives/Select';
  import { Kbd } from '$ui/primitives/Kbd';
  import { toast } from '$ui/primitives/Toast';

  let { store }: { store: SessionStore } = $props();

  // Auto-grow del textarea de nota: chico por defecto, crece con el contenido.
  function handleNoteInput(e: Event) {
    const el = e.currentTarget as HTMLTextAreaElement;
    store.setNotes(el.value);
    el.style.height = 'auto';
    el.style.height = Math.max(44, el.scrollHeight) + 'px';
  }

  let showTaxonomy = $derived(
    store.currentResult === 'incorrect' || store.currentResult === 'unresolved'
  );

  function rate(result: AttemptResult) {
    store.rateProblem(result);
  }

  // Flag para evitar doble-pulsación en NEXT PROBLEM mientras se persiste
  // el attempt (F9 §7.7 prevención de pulsaciones duplicadas).
  let submittingNext = $state(false);
  async function next() {
    if (submittingNext) return;
    submittingNext = true;
    try {
      store.nextProblem();
      // Dejar un tick de UI para que el botón muestre el estado loading
      await Promise.resolve();
    } finally {
      submittingNext = false;
    }
  }

  async function discard() {
    const snapshot = store.problem.elapsedMs;
    store.discardProblem();
    toast.info(t('rating.discardedToast'), {
      duration: 5000,
      action: {
        label: t('rating.discardedUndo'),
        onclick: () => {
          // Re-inicia el problema con el tiempo que tenía antes de descartar.
          // El store preserva el estado interno del problema; sólo hay que
          // re-activarlo sin incrementar el número.
          void snapshot;
          store.toggleProblem();
        }
      }
    });
  }

  /**
   * Variante semántica por outcome. Correct → success, incorrect → danger,
   * unresolved → info. Cada uno representa un estado del sistema con
   * significado cromático propio (F6 §22, F8 §3.3, F10 §6.1, Leyes §3.3).
   * El estado seleccionado se pinta filled; los demás, outline.
   */
  function outcomeClass(outcome: AttemptResult): string {
    const active = store.currentResult === outcome;
    const base = `rating-btn rating-btn--${outcome}`;
    return active ? `${base} rating-btn--active` : base;
  }
</script>

<section class="rating">
  <p class="rating-hint" id="rating-hint">{t('rating.hint')}</p>

  <div class="buttons-row" role="radiogroup" aria-label={t('rating.ariaLabel')} aria-describedby="rating-hint">
    <div class="result-cell">
      <Button
        variant="outline"
        size="md"
        class={outcomeClass('correct')}
        onclick={() => rate('correct')}
        ariaLabel={t('rating.correct') + ', ' + t('rating.shortcut1')}
      >
        {t('rating.correct')}
      </Button>
      <Kbd>1</Kbd>
    </div>
    <div class="result-cell">
      <Button
        variant="outline"
        size="md"
        class={outcomeClass('incorrect')}
        onclick={() => rate('incorrect')}
        ariaLabel={t('rating.incorrect') + ', ' + t('rating.shortcut2')}
      >
        {t('rating.incorrect')}
      </Button>
      <Kbd>2</Kbd>
    </div>
    <div class="result-cell">
      <Button
        variant="outline"
        size="md"
        class={outcomeClass('unresolved')}
        onclick={() => rate('unresolved')}
        ariaLabel={t('rating.unresolved') + ', ' + t('rating.shortcut3')}
      >
        {t('rating.unresolved')}
      </Button>
      <Kbd>3</Kbd>
    </div>
  </div>

  {#if showTaxonomy}
    <Select
      label={t('rating.taxonomyLabel')}
      value={store.currentErrorTaxonomy}
      options={[
        { value: '', label: t('rating.taxonomyNone') },
        ...STANDARD_TAXONOMY.map((tc) => ({ value: tc.code, label: tc.label }))
      ]}
      onchange={(v) => store.setErrorTaxonomy(v || null)}
    />
  {/if}

  <div class="note-field">
    <label for="rating-note" class="note-label">{t('rating.notesLabel')}</label>
    <textarea
      id="rating-note"
      class="note-input"
      rows="2"
      placeholder={t('rating.notesPlaceholder')}
      spellcheck={false}
      value={store.currentNotes}
      oninput={handleNoteInput}
    ></textarea>
  </div>

  <div class="actions">
    <Button
      variant="filled"
      size="lg"
      disabled={store.currentResult === null || submittingNext}
      loading={submittingNext}
      onclick={() => void next()}
      ariaLabel={store.currentResult === null ? t('rating.nextDisabled') : undefined}
    >
      {t('rating.next')}
    </Button>
    <Button variant="plain" size="lg" onclick={discard}>
      {t('rating.discard')}
    </Button>
  </div>
</section>

<style>
  .rating {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    width: 100%;
    max-width: 720px;
    margin: 0 auto;
  }

  /* El clock YA NO vive aquí — está en CronometroCiego y siempre está
     visible en el mismo lugar. RatingCard solo añade el contenido debajo. */

  /* Hint de calificación (F10 §1.7 Learnability, F6 §34 Nielsen #2).
     El usuario nuevo necesita saber QUÉ significan los números 1/2/3 antes
     de calificar — sin esto los `<Kbd>` bajo cada botón resultan crípticos. */
  .rating-hint {
    margin: 0;
    text-align: center;
    font-family: var(--font-body, sans-serif);
    font-size: var(--text-sm);
    color: var(--color-text-muted);
    line-height: 1.5;
  }

  .buttons-row {
    display: flex;
    gap: var(--space-3);
    width: 100%;
  }

  .result-cell {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-2);
  }

  /* Botones con texto + Kbd debajo. Color semántico por outcome. */
  .result-cell :global(.btn) {
    width: 100%;
  }

  .result-cell :global(.rating-btn--correct) {
    border-color: var(--color-success);
    color: var(--color-success);
    background: transparent;
  }
  .result-cell :global(.rating-btn--correct:hover:not([data-disabled])) {
    background: var(--color-success-tint);
  }
  .result-cell :global(.rating-btn--correct.rating-btn--active) {
    background: var(--color-success);
    border-color: var(--color-success);
    color: var(--color-success-fg);
  }

  .result-cell :global(.rating-btn--incorrect) {
    border-color: var(--color-brand);
    color: var(--color-brand);
    background: transparent;
  }
  .result-cell :global(.rating-btn--incorrect:hover:not([data-disabled])) {
    background: var(--color-brand-tint);
  }
  .result-cell :global(.rating-btn--incorrect.rating-btn--active) {
    background: var(--color-brand);
    border-color: var(--color-brand);
    color: var(--color-brand-fg);
  }

  .result-cell :global(.rating-btn--unresolved) {
    border-color: var(--color-info);
    color: var(--color-info);
    background: transparent;
  }
  .result-cell :global(.rating-btn--unresolved:hover:not([data-disabled])) {
    background: var(--color-info-tint);
  }
  .result-cell :global(.rating-btn--unresolved.rating-btn--active) {
    background: var(--color-info);
    border-color: var(--color-info);
    color: var(--color-info-fg);
  }

  .actions {
    display: flex;
    gap: var(--space-2);
    align-items: center;
    justify-content: flex-end;
  }

  /* Note field: compacto por defecto, crece con el contenido. */
  .note-field {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    width: 100%;
  }

  .note-label {
    font-size: 0.75rem;
    font-weight: var(--weight-semibold, 600);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--color-text-muted);
  }

  .note-input {
    width: 100%;
    min-height: 44px;
    max-height: 200px;
    padding: var(--space-3);
    font-family: var(--font-body, sans-serif);
    font-size: var(--text-body);
    color: var(--color-text-primary);
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-sm);
    outline: none;
    resize: vertical;
    overflow-y: auto;
    transition: border-color var(--dur-fast) var(--ease-out);
  }

  .note-input::placeholder {
    color: var(--color-text-faint);
  }

  .note-input:focus-visible {
    border-color: var(--color-accent);
    outline: 2px solid var(--color-accent-tint);
    outline-offset: 0;
  }
</style>